import { useCallback, useMemo } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import { ContactResult } from "../../../types/ContactResult";
import { useObx } from "../../../hooks/useObx";
import ApiRequestPaths from "../../../constants/ApiRequestPaths";

export interface IUseCreateContactResultProps {
  /** The id of the session's contact. */
  contactId: string;
}

/**
 * Local type to match the backend API's expected request structure.
 * The API still expects the pre-refactored field names (opportunityId instead of leadId).
 */
interface ContactResultRequest {
  id?: string;
  appointmentId?: string;
  customerId: string;
  /** Intentionally using `opportunityId` instead of `leadId` to maintain consistency with the backend API. */
  opportunityId: string;
  type: number;
  disqualifyingReasons: Record<string, boolean>;
}

/**
 * Local type to match the backend API's response structure.
 * The API returns the pre-refactored field names (opportunityId instead of leadId).
 */
interface ContactResultResponse {
  id?: string;
  appointmentId?: string;
  customerId: string;
  opportunityId: string;
  type: number;
  disqualifyingReasons: Record<string, boolean>;
}

/** A hook which dispatches the result for the session's contact to the OBX server. */
const useCreateContactResult: TriggeredHook<ContactResult, ContactResult, boolean, IUseCreateContactResultProps> = ({
  contactId
}) => {
  const { res, err, loading, setReq } = useObx<ContactResultRequest, ContactResultResponse>("Post Contact Result", {
    method: "post",
    url: ApiRequestPaths.postContactResult(contactId)
  });

  // Map the API response back from old nomenclature to new ContactResult shape
  const mapResponseToContactResult = useCallback(
    (apiResponse: ContactResultResponse | undefined): ContactResult | null => {
      if (!apiResponse) return null;
      const contactResult = new ContactResult(apiResponse.customerId, apiResponse.opportunityId, apiResponse.type);
      contactResult.id = apiResponse.id;
      contactResult.appointmentId = apiResponse.appointmentId;
      contactResult.disqualifyingReasons = apiResponse.disqualifyingReasons;
      return contactResult;
    },
    []
  );

  const execute = useCallback((contactResult: ContactResult) => {
    // Map ContactResult to the API's expected ContactResultRequest format
    const contactResultRequest: ContactResultRequest = {
      id: contactResult.id,
      appointmentId: contactResult.appointmentId,
      customerId: contactResult.customerId,
      opportunityId: contactResult.leadId,
      type: contactResult.type,
      disqualifyingReasons: contactResult.disqualifyingReasons
    };
    setReq(r => r && { ...r, payload: contactResultRequest });
  }, []);

  const result = useMemo(() => mapResponseToContactResult(res?.payload), [res?.payload, mapResponseToContactResult]);

  return useMemo(() => ({ result, error: Boolean(err), isLoading: loading, execute }), [result, err, loading, execute]);
};

export default useCreateContactResult;
